import { CommonModule, Location } from '@angular/common';
import { Component, DestroyRef, Input, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterLink } from '@angular/router';
import { Observable, Subscription, interval, startWith, switchMap, takeWhile } from 'rxjs';

import { UI, type UiBadgeVariant, type UiTab } from '../../components/ui';
import type { EntityConfig } from '../../config/entity-config';
import {
  CampaignService,
  type CampaignDetail,
  type CampaignLine,
  type CampaignProgress,
  type CampaignReport,
} from '../../services/campaign.service';
import { MailingService } from '../../services/mailing.service';
import { RoleAccessService } from '../../services/role-access.service';
import { ShellRoutesService } from '../../services/shell-routes.service';
import { goBackOrNavigate } from '../../utils/navigation';
import { apiErrorMessage } from '../campaign-create/campaign-create.component';

const POLL_MS = 3000;

function choice(value: unknown): string {
  if (value && typeof value === 'object' && 'value' in value) {
    return String((value as { value?: unknown }).value ?? '');
  }
  return typeof value === 'string' ? value : '';
}

/** Campaign page: start/cancel/retry, live progress while it runs, the JSON report, its lines and its messages. */
@Component({
  selector: 'app-campaign-detail',
  standalone: true,
  imports: [CommonModule, RouterLink, ...UI],
  template: `
    @if (campaign(); as c) {
      <section class="px-4 pt-3">
        <div class="mb-3 flex flex-wrap items-center gap-2">
          <button type="button" uiButton variant="outline" size="sm" (click)="goBack()">Back</button>
          <h1 class="m-0 font-heading text-lg font-semibold text-fg">{{ c.name }}</h1>
          <code class="text-xs text-muted">{{ c.reference }}</code>
          <ui-badge variant="info">{{ choice(c.channel) }}</ui-badge>
          <ui-badge [variant]="statusVariant(status())">{{ status() }}</ui-badge>
        </div>

        @if (message()) {
          <ui-alert class="mb-3 block" variant="info">{{ message() }}</ui-alert>
        }

        <ui-card title="Send">
          <div header-actions class="flex flex-wrap gap-2">
            <button type="button" uiButton variant="outline" size="sm" (click)="download()">Download result</button>
            <a uiButton variant="outline" size="sm" [routerLink]="shellRoutes.commands('manage', 'message-queues', 'list')" [queryParams]="{ campaign: c.id }">
              View messages
            </a>
            @if (canAct()) {
              @if (status() === 'READY') {
                <button type="button" uiButton size="sm" [disabled]="busy()" (click)="start()">
                  Start sending ({{ report().parsed.valid }})
                </button>
              }
              @if (status() === 'PARTIAL' || status() === 'FAILED') {
                <button type="button" uiButton variant="outline" size="sm" [disabled]="busy()" (click)="retry()">
                  Retry failed
                </button>
              }
              @if (status() === 'PROCESSING') {
                <button type="button" uiButton variant="outline" size="sm" [disabled]="busy()" (click)="pause()">
                  Pause
                </button>
              }
              @if (status() === 'PAUSED') {
                <button type="button" uiButton size="sm" [disabled]="busy()" (click)="resume()">Resume</button>
              }
              @if (status() === 'READY' || status() === 'PROCESSING' || status() === 'PAUSED') {
                <button type="button" uiButton variant="danger" size="sm" [disabled]="busy()" (click)="cancel()">
                  Cancel
                </button>
              }
            }
          </div>
          <div class="h-3.5 w-full overflow-hidden rounded-full bg-surface-muted" role="progressbar" [attr.aria-valuenow]="pct()">
            <div class="h-full bg-brand transition-all" [style.width.%]="pct()"></div>
          </div>
          <p class="mb-1 mt-2 text-sm text-fg">
            <strong>{{ pct() }}%</strong>
            <span class="text-muted">
              — {{ report().processed.queued }} queued ·
              <span class="text-success">{{ report().processed.sent }} sent</span> ·
              <span class="text-danger">{{ report().processed.failed }} failed</span> ·
              {{ report().processed.delivered }} delivered
            </span>
          </p>
        </ui-card>

        <ui-card title="File report" class="mt-3 block">
          <div class="grid grid-cols-2 gap-3 md:grid-cols-6">
            @for (t of tiles(); track t.label) {
              <div class="rounded-md border border-border p-2 text-center">
                <div class="text-xl font-semibold" [class]="t.cls">{{ t.value }}</div>
                <div class="text-xs text-muted">{{ t.label }}</div>
              </div>
            }
          </div>
          <p class="mb-0 mt-2 text-xs text-muted">
            File <code>{{ c.original_filename }}</code>
            @if (report().period?.start) {
              · period {{ report().period?.start }} → {{ report().period?.end }}
            }
            @if (report().timing.duration_s !== undefined) {
              · sent in {{ report().timing.duration_s }}s
            }
          </p>
          @if (breakdown(); as b) {
            <p class="mb-0 mt-1 text-xs text-muted">{{ b }}</p>
          }
        </ui-card>

        <ui-tabs class="mt-3" [tabs]="tabItems" [active]="tab()" (activeChange)="onTab($event)" />
        <div uiTableWrap class="mt-3">
          @if (tab() === 'issues') {
            <table uiTable>
              <thead>
                <tr><th>Line</th><th>Name</th><th>Recipient</th><th>Reason</th></tr>
              </thead>
              <tbody>
                @for (i of report().issues; track i.line) {
                  <tr>
                    <td>{{ i.line }}</td>
                    <td>{{ i.name }}</td>
                    <td>{{ i.recipient }}</td>
                    <td class="text-xs text-muted">{{ i.code }} — {{ i.detail }}</td>
                  </tr>
                } @empty {
                  <tr><td colspan="4" class="text-muted">No rejected row.</td></tr>
                }
              </tbody>
            </table>
          } @else {
            <div class="mb-2 flex items-center gap-2">
              <label class="text-sm text-muted" for="line-status">Status</label>
              <select uiInput id="line-status" class="w-48" [value]="lineStatus()" (change)="filterLines($any($event.target).value)">
                <option value="">All</option>
                @for (s of lineStatuses; track s) {
                  <option [value]="s">{{ s }}</option>
                }
              </select>
            </div>
            <table uiTable>
              <thead>
                <tr><th>Line</th><th>Recipient</th><th>Status</th><th>Comment</th><th></th></tr>
              </thead>
              <tbody>
                @for (line of lines(); track line.id) {
                  <tr>
                    <td>{{ line.line_index }}</td>
                    <td>{{ line.recipient_display }}</td>
                    <td>
                      <ui-badge [variant]="lineVariant(choice(line.status))">{{ choice(line.status) }}</ui-badge>
                    </td>
                    <td class="text-xs text-muted">{{ line.error }}</td>
                    <td>
                      @if (line.message_queue_id) {
                        <a class="text-brand hover:underline" [routerLink]="shellRoutes.commands('manage', 'message-queues', line.message_queue_id)">
                          message
                        </a>
                      }
                    </td>
                  </tr>
                } @empty {
                  <tr><td colspan="5" class="text-muted">No line.</td></tr>
                }
              </tbody>
            </table>
          }
        </div>
      </section>
    } @else if (error()) {
      <ui-alert class="m-4 block" variant="danger">{{ error() }}</ui-alert>
    }
  `,
})
export class CampaignDetailComponent implements OnInit {
  @Input() entityConfig!: EntityConfig;
  @Input() id!: string;

  private readonly svc = inject(CampaignService);
  private readonly mailings = inject(MailingService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly location = inject(Location);
  private readonly router = inject(Router);
  private readonly roleAccess = inject(RoleAccessService);
  readonly shellRoutes = inject(ShellRoutesService);
  readonly choice = choice;
  private pollSub?: Subscription;

  readonly lineStatuses = ['VALID', 'INVALID', 'QUEUED', 'SENDING', 'SENT', 'FAILED', 'CANCELLED'];
  readonly tabItems: UiTab[] = [
    { id: 'lines', label: 'Lines' },
    { id: 'issues', label: 'Rejected rows' },
  ];

  readonly campaign = signal<CampaignDetail | null>(null);
  readonly progress = signal<CampaignProgress | null>(null);
  readonly lines = signal<CampaignLine[]>([]);
  readonly tab = signal('lines');
  readonly lineStatus = signal('');
  readonly busy = signal(false);
  readonly message = signal<string | null>(null);
  readonly error = signal<string | null>(null);

  readonly status = computed(() => this.progress()?.status ?? choice(this.campaign()?.status));
  readonly report = computed<CampaignReport>(
    () => this.progress()?.execution_report ?? (this.campaign() as CampaignDetail).execution_report,
  );
  readonly pct = computed(() => Math.round(this.progress()?.completion_percentage ?? this.campaign()?.completion_percentage ?? 0));
  readonly canAct = computed(() => this.roleAccess.isActiveSystemRole());
  readonly tiles = computed(() => {
    const parsed = this.report().parsed;
    return [
      { label: 'Rows parsed', value: parsed.rows_parsed, cls: '' },
      { label: 'With recipient', value: parsed.with_recipient, cls: '' },
      { label: 'Skipped (none)', value: parsed.skipped_no_recipient, cls: 'text-muted' },
      { label: 'Valid', value: parsed.valid, cls: 'text-success' },
      { label: 'Invalid', value: parsed.invalid, cls: parsed.invalid ? 'text-danger' : '' },
      { label: 'Repeated', value: parsed.duplicates, cls: parsed.duplicates ? 'text-warning' : '' },
    ];
  });
  readonly breakdown = computed(() => {
    const processed = this.report().processed;
    const parts: string[] = [];
    for (const [label, values] of [
      ['providers', processed.by_provider],
      ['countries', processed.by_country],
      ['delivery', processed.by_delivery_status],
    ] as const) {
      const text = Object.entries(values ?? {})
        .map(([key, count]) => `${key} ${count}`)
        .join(', ');
      if (text) {
        parts.push(`${label}: ${text}`);
      }
    }
    return parts.join(' · ');
  });

  ngOnInit(): void {
    this.svc.detail(this.id).subscribe({
      next: (campaign) => {
        this.campaign.set(campaign);
        this.loadLines();
        this.follow();
      },
      error: () => this.error.set('Could not load this campaign.'),
    });
  }

  goBack(): void {
    goBackOrNavigate(this.location, this.router, this.shellRoutes.commands('manage', this.entityConfig.key));
  }

  statusVariant(status: string): UiBadgeVariant {
    if (status === 'COMPLETED') return 'success';
    if (status === 'FAILED' || status === 'VALIDATION_FAILED') return 'danger';
    if (status === 'PROCESSING' || status === 'PARTIAL') return 'warning';
    if (status === 'READY') return 'info';
    return 'neutral';
  }

  lineVariant(status: string): UiBadgeVariant {
    if (status === 'SENT') return 'success';
    if (status === 'FAILED' || status === 'INVALID') return 'danger';
    if (status === 'QUEUED' || status === 'SENDING') return 'warning';
    return 'neutral';
  }

  onTab(id: string): void {
    this.tab.set(id);
  }

  filterLines(value: string): void {
    this.lineStatus.set(value);
    this.loadLines();
  }

  start(): void {
    if (!confirm(`Send ${this.report().parsed.valid} ${choice(this.campaign()?.channel)} message(s) now?`)) {
      return;
    }
    this.act(this.svc.start(this.id), 'Sending started.');
  }

  pause(): void {
    this.act(this.mailings.pauseCampaign(this.id), 'Campaign paused. Queued messages are held until you resume.');
  }

  resume(): void {
    this.act(this.mailings.resumeCampaign(this.id), 'Campaign resumed.');
  }

  cancel(): void {
    this.act(this.svc.cancel(this.id), 'Campaign cancelled. Messages already handed to providers are not recalled.');
  }

  retry(): void {
    this.act(this.svc.retry(this.id), 'Failed lines queued again.');
  }

  download(): void {
    this.svc.result(this.id).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${this.campaign()?.reference ?? 'campaign'}-result.csv`;
        a.click();
        URL.revokeObjectURL(url);
      },
      error: () => this.message.set('Could not download the result file.'),
    });
  }

  private act(call: Observable<CampaignDetail>, text: string): void {
    this.busy.set(true);
    call.subscribe({
      next: (campaign) => {
        this.campaign.set(campaign);
        this.progress.set(null);
        this.message.set(text);
        this.busy.set(false);
        this.loadLines();
        this.follow();
      },
      error: (err) => {
        this.message.set(apiErrorMessage(err, 'The action failed.'));
        this.busy.set(false);
      },
    });
  }

  /** Refresh progress every few seconds while the campaign is sending (one fetch otherwise). */
  private follow(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = interval(POLL_MS)
      .pipe(
        startWith(0),
        switchMap(() => this.svc.progress(this.id)),
        takeWhile((p) => p.status === 'PROCESSING', true),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (p) => {
          const wasActive = this.status() === 'PROCESSING';
          this.progress.set(p);
          if (wasActive && p.status !== 'PROCESSING') {
            this.loadLines();
          }
        },
      });
  }

  private loadLines(): void {
    this.svc.lines(this.id, 1, this.lineStatus()).subscribe({
      next: (page) => this.lines.set(page.results),
      error: () => this.lines.set([]),
    });
  }
}
